import { useMemo } from 'react';
import { Spinner, Text } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { RequestCard } from '../RequestCard';
import { EmptyState } from '../EmptyState';
import type { RequestListProps, DashboardTab } from '@/types/events.types';
import { buildSeriesOrdinalTitleMap } from '@/utils/seriesEventTitle';
import { useRequestListStyles } from './RequestList.styles';

interface ExtendedRequestListProps extends RequestListProps {
  tab: DashboardTab;
  onCreateRequest?: (() => void) | undefined;
  onRetry?: (() => void | Promise<void>) | undefined;
}

export const RequestList = ({
  events,
  loading,
  error,
  onLoadMore,
  hasMore,
  onView,
  onDelete,
  onCancel,
  tab,
  onCreateRequest,
  onRetry,
  figmaVariant = false,
}: ExtendedRequestListProps) => {
  const styles = useRequestListStyles();
  const { t } = useTranslation('dashboard');
  const displayTitleByEventId = useMemo(
    () =>
      buildSeriesOrdinalTitleMap(
        events,
        (event) =>
          event.eventTitle ||
          (event.eventState === 'DRAFT' ? t('card.untitledDraft') : 'Untitled Event')
      ),
    [events, t]
  );

  if (loading && events.length === 0) {
    return (
      <div className={styles.loading} role="status" aria-label={t('loading')}>
        <Spinner label={t('loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error} role="alert">
        <Text className={styles.errorText} weight="semibold">
          {t('error.title')}
        </Text>
        <Text>{t('error.description')}</Text>
        {onRetry && (
          <BasicButton variant="primary" size="medium" onClick={() => void onRetry()}>
            {t('error.retry')}
          </BasicButton>
        )}
      </div>
    );
  }

  if (events.length === 0) {
    return <EmptyState tab={tab} onCreateRequest={onCreateRequest} figmaVariant={figmaVariant} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.list} role="list" aria-label={`${tab} requests`}>
        {events.map((event) => (
          <div key={event.eventId} role="listitem">
            <RequestCard
              event={{
                ...event,
                eventTitle: displayTitleByEventId.get(event.eventId) ?? event.eventTitle,
              }}
              onView={onView}
              onDelete={onDelete}
              onCancel={onCancel ? (eventId: string) => void onCancel(eventId) : undefined}
              figmaVariant={figmaVariant}
            />
          </div>
        ))}
      </div>
      {hasMore && onLoadMore && (
        <div className={styles.loadMore}>
          <BasicButton
            variant="outline"
            size="medium"
            onClick={() => void onLoadMore()}
            disabled={loading}
          >
            {loading ? <Spinner size="tiny" /> : t('loadMore')}
          </BasicButton>
        </div>
      )}
    </div>
  );
};

RequestList.displayName = 'RequestList';
